'use client'

import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

export interface SetGoalsModalProps {
  channelKey: string
  channelName: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved?: (targetRoas: number) => void
}

export function SetGoalsModal({
  channelKey,
  channelName,
  open,
  onOpenChange,
  onSaved,
}: SetGoalsModalProps) {
  const [targetRoas, setTargetRoas] = useState<string>('')
  const [initialLoading, setInitialLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Load existing goal when the modal opens.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setInitialLoading(true)
    fetch(`/api/channels/${channelKey}/goals`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return
        if (data && typeof data.target_roas === 'number') {
          setTargetRoas(String(data.target_roas))
        } else {
          setTargetRoas('')
        }
      })
      .catch(() => { /* silent — empty form is fine */ })
      .finally(() => { if (!cancelled) setInitialLoading(false) })
    return () => { cancelled = true }
  }, [open, channelKey])

  async function handleSave() {
    const parsed = Number(targetRoas)
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error('Enter a target ROAS greater than 0 (e.g. 3.5).')
      return
    }
    setSaving(true)
    try {
      const res = await fetch(`/api/channels/${channelKey}/goals`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_roas: parsed }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Unknown' }))
        throw new Error(err.error ?? 'Save failed')
      }
      toast.success(`${channelName} ROAS target set to ${parsed.toFixed(1)}×`)
      onSaved?.(parsed)
      onOpenChange(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save goal')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Set your ROAS</DialogTitle>
          <DialogDescription>
            Set a target return on ad spend for {channelName}. We'll use this as the benchmark for performance and recommendations on this channel.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 flex flex-col gap-2">
          <label htmlFor="target-roas" className="text-[13px] font-medium text-text">
            Target ROAS
          </label>
          <div className="flex items-center gap-2">
            <Input
              id="target-roas"
              type="number"
              inputMode="decimal"
              step="0.1"
              min="0.1"
              placeholder="e.g. 3.5"
              value={targetRoas}
              onChange={(e) => setTargetRoas(e.target.value)}
              disabled={initialLoading || saving}
              className="max-w-[160px]"
            />
            <span className="text-[14px] font-semibold text-text-muted">×</span>
          </div>
          <p className="text-[12px] text-text-muted-2">
            Revenue divided by spend. A target of 3.5× means $3.50 of revenue for every $1 spent.
          </p>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={initialLoading || saving}>
            {saving ? 'Saving…' : 'Save goal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
